<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Spinner } from '@drama/ui'
import { NOVEL_GENRE_LIMIT } from '@drama/contracts/novel'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  load_tags: { type: Function, default: null },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])
const { t, te, locale } = useI18n()
const available_tags = ref([])
const retained_tags = [...props.modelValue]
const loading = ref(false)
const failed = ref(false)
const selected = computed(() => new Set(props.modelValue))
// Retain stored tags even when they no longer occur in the public catalog.
const choices = computed(() => [...new Set([...retained_tags, ...props.modelValue, ...available_tags.value])]
  .map(value => ({ value, label: tagLabel(value) }))
  .sort((left, right) => left.label.localeCompare(right.label, locale.value)))
let generation = 0

function tagLabel(value) {
  const key = `novelLibrary.genres.${value}`
  return te(key) ? t(key) : value
}

function toggle(value, checked) {
  if (props.disabled) return
  if (checked && (selected.value.has(value) || selected.value.size >= NOVEL_GENRE_LIMIT)) return
  emit('update:modelValue', checked ? [...props.modelValue, value] : props.modelValue.filter(tag => tag !== value))
}

async function load() {
  const request_generation = ++generation
  loading.value = Boolean(props.load_tags)
  failed.value = false
  if (!props.load_tags) return
  try {
    const tags = await props.load_tags()
    if (request_generation === generation) available_tags.value = tags
  } catch {
    if (request_generation === generation) failed.value = true
  } finally {
    if (request_generation === generation) loading.value = false
  }
}

onMounted(load)
watch(() => props.load_tags, load)
onBeforeUnmount(() => { generation += 1 })
</script>

<template>
  <div class="novel-tag-picker" data-path="genres" data-testid="novel-tag-picker" tabindex="-1">
    <fieldset class="novel-tag-picker__choices" :disabled="disabled" :aria-invalid="invalid ? 'true' : undefined">
      <legend class="novel-tag-picker__legend">{{ t('novelAuthoring.genres') }}</legend>
      <label
        v-for="choice in choices"
        :key="choice.value"
        class="novel-tag-picker__choice"
        :class="{ 'is-selected': selected.has(choice.value) }"
      >
        <input
          type="checkbox"
          :value="choice.value"
          :checked="selected.has(choice.value)"
          :disabled="!selected.has(choice.value) && selected.size >= NOVEL_GENRE_LIMIT"
          data-testid="novel-tag-option"
          @change="toggle(choice.value, $event.target.checked)"
        >
        <span>{{ choice.label }}</span>
      </label>
    </fieldset>
    <div v-if="loading" class="novel-tag-picker__status" role="status">
      <Spinner size="sm" :label="t('novelAuthoring.tagsLoading')" />
      <span>{{ t('novelAuthoring.tagsLoading') }}</span>
    </div>
    <div v-else-if="failed" class="novel-tag-picker__status" role="alert">
      <span>{{ t('novelAuthoring.tagsLoadFailed') }}</span>
      <Button variant="ghost" size="sm" :disabled="disabled" data-testid="novel-tag-retry" @click="load">{{ t('novelAuthoring.tagsRetry') }}</Button>
    </div>
    <p v-else-if="!choices.length" class="novel-tag-picker__empty">{{ t('novelAuthoring.tagsEmpty') }}</p>
  </div>
</template>

<style scoped>
.novel-tag-picker { display: grid; min-width: 0; gap: var(--drama-space-sm); }
.novel-tag-picker:focus { outline: 2px solid var(--drama-color-primary); outline-offset: var(--drama-space-xs); }
.novel-tag-picker__choices { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); min-width: 0; margin: 0; padding: 0; border: 0; }
.novel-tag-picker__legend { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.novel-tag-picker__choice { display: inline-flex; min-height: var(--drama-control-height-sm); max-width: 100%; align-items: center; gap: var(--drama-space-xs); padding: var(--drama-space-xs) var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); cursor: pointer; }
.novel-tag-picker__choice span { overflow-wrap: anywhere; }
.novel-tag-picker__choice input { flex: 0 0 auto; margin: 0; accent-color: var(--drama-color-primary); }
.novel-tag-picker__choice:hover, .novel-tag-picker__choice.is-selected { border-color: var(--drama-color-primary); background: var(--drama-color-surface-alt); color: var(--drama-color-text); }
.novel-tag-picker__choice:focus-within { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
.novel-tag-picker__choice:has(input:disabled) { opacity: .55; cursor: default; }
.novel-tag-picker__status { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.novel-tag-picker__empty { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
</style>
